import React, { memo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
} from '@mui/material';

interface FixedShiftConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<boolean>;
  isLoading: boolean;
}

/**
 * 固定シフト確定ダイアログ
 *
 * 医師シフトルール由来 (source=rule) の医師シフトのみを確定し、
 * 日次枠の確定枠数 (confirmed_slots) へ加算する。
 * 確定後は残枠のみを通常募集で埋める運用に切り替わる。
 */
const FixedShiftConfirmDialog: React.FC<FixedShiftConfirmDialogProps> = memo(({
  open,
  onClose,
  onConfirm,
  isLoading,
}) => {
  const handleConfirm = useCallback(async () => {
    const success = await onConfirm();
    if (success) {
      onClose();
    }
  }, [onConfirm, onClose]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>固定シフトを確定しますか？</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          医師シフトルールから反映された固定シフトのみを確定します。
          確定後、対象枠の空き部分は通常募集で埋めることができます。
        </Typography>
        <Alert severity="warning" sx={{ mt: 1 }}>
          確定した固定シフトは以降編集できません。
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleConfirm}
          disabled={isLoading}
        >
          {isLoading ? '確定中...' : '固定シフトを確定'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

FixedShiftConfirmDialog.displayName = 'FixedShiftConfirmDialog';

export default FixedShiftConfirmDialog;
